import { Autocomplete, Grid, TextField } from "@mui/material";
import { FormikTextField, useResizableFieldMaxHeight } from "@shared";

import { ClientDetailResponse, EmailTemplateResponse } from "@/api";
import { FormikAutocomplete } from "@/common/components/fields/formik/formik-autocomplete/FormikAutocomplete";
import { FormDialog } from "@/common/components/form-dialog/FormDialog";
import { useTranslation } from "@/translations";

import { useEmailSend } from "./hooks/useEmailSend";

interface Props {
  client: ClientDetailResponse;
  onClose: () => void;
}

export function EmailSendDialog({ client, onClose }: Props) {
  const { t } = useTranslation();
  const { formik, emailOptions, templates, selectedTemplate, handleSelectTemplate } = useEmailSend(client, onClose);
  const { setFieldRef, maxHeight } = useResizableFieldMaxHeight();

  return (
    <FormDialog
      title={t("clients.client.email.dialog.title")}
      onClose={onClose}
      formik={formik}
      submitLabel={t("clients.client.email.dialog.openInMail")}
    >
      <Grid container spacing={3} sx={{ pt: 1, minWidth: "500px" }}>
        <Grid size={12}>
          <FormikAutocomplete
            formik={formik}
            fieldName="recipientEmail"
            label={t("clients.client.email.dialog.recipientEmail")}
            options={emailOptions}
            freeSolo
            required
          />
        </Grid>
        <Grid size={12}>
          <Autocomplete<EmailTemplateResponse>
            options={templates}
            value={selectedTemplate}
            getOptionLabel={(o) => o.title}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, selected) => handleSelectTemplate(selected)}
            noOptionsText={t("settings.templates.noTemplates")}
            renderInput={(params) => (
              <TextField {...params} label={t("clients.client.email.dialog.selectTemplate")} size="small" />
            )}
          />
        </Grid>
        <Grid size={12}>
          <FormikTextField formik={formik} fieldName="subject" label={t("clients.client.email.dialog.subject")} />
        </Grid>
        <Grid size={12}>
          <FormikTextField
            formik={formik}
            fieldName="body"
            label={t("clients.client.email.dialog.messageContent")}
            multiline
            rows={6}
            inputRef={setFieldRef}
            sx={{
              "& .MuiInputBase-inputMultiline": { resize: "vertical", maxHeight },
            }}
          />
        </Grid>
      </Grid>
    </FormDialog>
  );
}
